import { MANILA_TIME_ZONE } from './dateTime';
import { isCorporateBooking, resolveBookingWorkflowStage } from './bookingStage';

export const EVENT_STATUS_FILTER_ALL = 'all';

export const EVENT_PROGRESS_STAGES = {
  REQUEST_SUBMITTED: 'request_submitted',
  ADMIN_REVIEW: 'admin_review',
  INITIAL_CONSULTATION: 'initial_consultation',
  PROPOSAL_CONTRACT: 'proposal_contract',
  EVENT_PREPARATION: 'event_preparation',
  EVENT_LIVE: 'event_live',
  REJECTED: 'declined',
};

export const EVENT_STATUS_FILTER_OPTIONS = [
  { value: EVENT_STATUS_FILTER_ALL, label: 'All' },
  { value: EVENT_PROGRESS_STAGES.REQUEST_SUBMITTED, label: 'Request Submitted' },
  { value: EVENT_PROGRESS_STAGES.ADMIN_REVIEW, label: 'Admin Review' },
  { value: EVENT_PROGRESS_STAGES.INITIAL_CONSULTATION, label: 'Initial Consultation' },
  { value: EVENT_PROGRESS_STAGES.PROPOSAL_CONTRACT, label: 'Proposal & Contract' },
  { value: EVENT_PROGRESS_STAGES.EVENT_PREPARATION, label: 'Event Preparation' },
  { value: EVENT_PROGRESS_STAGES.EVENT_LIVE, label: 'Event is Now Live!' },
  { value: EVENT_PROGRESS_STAGES.REJECTED, label: 'Rejected' },
];

const statusToStage = {
  new: 'request_submitted',
  pending_review: 'admin_review',
  in_review: 'admin_review',
  approved: 'event_preparation',
  accepted: 'event_preparation',
  initial_consultation: 'initial_consultation',
  proposal_quotation: 'proposal_contract',
  agreement_confirmation: 'proposal_contract',
  contract_signing: 'proposal_contract',
  proposal_contract: 'proposal_contract',
  declined: 'declined',
};

const knownListStages = new Set([
  'pending_review',
  'admin_review',
  'request_submitted',
  'initial_consultation',
  'proposal_contract',
  'proposal_quotation',
  'agreement_confirmation',
  'contract_signing',
  'event_preparation',
  'accepted',
  'event_live',
  'declined',
  'cancelled',
]);

const progressStageAliases = {
  request_submitted: EVENT_PROGRESS_STAGES.REQUEST_SUBMITTED,
  new: EVENT_PROGRESS_STAGES.REQUEST_SUBMITTED,
  admin_review: EVENT_PROGRESS_STAGES.ADMIN_REVIEW,
  pending_review: EVENT_PROGRESS_STAGES.ADMIN_REVIEW,
  in_review: EVENT_PROGRESS_STAGES.ADMIN_REVIEW,
  initial_consultation: EVENT_PROGRESS_STAGES.INITIAL_CONSULTATION,
  proposal_contract: EVENT_PROGRESS_STAGES.PROPOSAL_CONTRACT,
  proposal_quotation: EVENT_PROGRESS_STAGES.PROPOSAL_CONTRACT,
  agreement_confirmation: EVENT_PROGRESS_STAGES.PROPOSAL_CONTRACT,
  contract_signing: EVENT_PROGRESS_STAGES.PROPOSAL_CONTRACT,
  event_preparation: EVENT_PROGRESS_STAGES.EVENT_PREPARATION,
  accepted: EVENT_PROGRESS_STAGES.EVENT_PREPARATION,
  approved: EVENT_PROGRESS_STAGES.EVENT_PREPARATION,
  event_live: EVENT_PROGRESS_STAGES.EVENT_LIVE,
  declined: EVENT_PROGRESS_STAGES.REJECTED,
};

/** Same stage the booking list badge uses. */
export const resolveClientListStage = (booking) => resolveBookingWorkflowStage(booking, {
  statusToStage,
  fallbackStage: 'pending_review',
  isKnownStage: (stage) => knownListStages.has(stage),
});

const getProgressFilterStage = (booking) => {
  const stage = booking?.displayStatus || resolveClientListStage(booking);
  return progressStageAliases[stage] || stage;
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const getManilaDayKeyFromDate = (date) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: MANILA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

/** Asia/Manila YYYY-MM-DD for a booking's event_date (date-only preferred). */
export const getBookingEventDayKey = (booking) => {
  const raw = String(booking?.event_date || '').trim();
  if (!raw) return null;

  const dateOnly = raw.slice(0, 10);
  if (DATE_ONLY_PATTERN.test(dateOnly) && (raw.length === 10 || raw[10] === 'T' || raw[10] === ' ')) {
    return dateOnly;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return getManilaDayKeyFromDate(parsed);
};

/** Non-corporate (concert, legacy empty, etc.) counts as Concert for the Concert toggle. */
export const isConcertBookingType = (booking) => !isCorporateBooking(booking);

export const matchesBookingTypeFilter = (booking, { concert = true, corporate = true } = {}) => {
  if (isCorporateBooking(booking)) return Boolean(corporate);
  return Boolean(concert);
};

export const filterClientBookings = (
  bookings = [],
  {
    concert = true,
    corporate = true,
    eventStatus = EVENT_STATUS_FILTER_ALL,
  } = {}
) => (
  (bookings || []).filter((booking) => (
    matchesBookingTypeFilter(booking, { concert, corporate })
    && (
      eventStatus === EVENT_STATUS_FILTER_ALL
      || getProgressFilterStage(booking) === eventStatus
    )
  ))
);

export const getFinalStageLabel = (booking) => (
  isCorporateBooking(booking) ? 'Event is Ready to Go!' : 'Event is Now Live!'
);

export const getFinalStageHelperText = (booking) => (
  isCorporateBooking(booking) ? 'Your event is ready to go!' : 'Your event is now live!'
);
